import React, { useState } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Card, CardContent, TextField, Button, Typography, Alert, Stack, Link,
} from '@mui/material';
import Logo from '../components/Logo';
import VolverInicio from '../components/VolverInicio';
import { api } from '../config/api';
import { leerError } from '../utils/errores';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const RecuperarContrasena: React.FC = () => {
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get('email') || '');
  const [error, setError] = useState('');
  const [enviado, setEnviado] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const limpio = email.trim();
    if (!limpio) return setError('Ingresa el email con el que te registraste.');
    if (!EMAIL_RE.test(limpio)) return setError('Revisa el formato del email (ej: nombre@empresa.cl).');
    setLoading(true);
    try {
      const { data } = await api.post('/auth/forgot-password', { email: limpio });
      setEnviado(data.message || 'Si existe una cuenta con ese email, te enviamos un enlace para restablecer tu contraseña.');
    } catch (err: any) {
      setError(leerError(err, 'No pudimos enviar el correo. Intenta de nuevo.').message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, background: 'linear-gradient(135deg, #075E54, #25D366)' }}>
      <Card sx={{ width: '100%', maxWidth: 420 }}>
        <CardContent sx={{ p: 4 }}>
          <VolverInicio />
          <Stack alignItems="center" spacing={1} sx={{ mb: 3 }}>
            <Logo size={44} />
            <Typography variant="h5" sx={{ pt: 1 }}>Recuperar contraseña</Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Te enviaremos un enlace para crear una nueva contraseña.
            </Typography>
          </Stack>

          {enviado ? (
            <Stack spacing={2}>
              <Alert severity="success">{enviado}</Alert>
              <Typography variant="body2" color="text.secondary">
                El enlace vence en 60 minutos. ¿No llegó? Espera un minuto y{' '}
                <Link component="button" type="button" onClick={() => setEnviado('')} sx={{ verticalAlign: 'baseline' }}>
                  vuelve a solicitarlo
                </Link>.
              </Typography>
              <Button component={RouterLink} to={`/login?email=${encodeURIComponent(email.trim())}`} variant="outlined">
                Volver a iniciar sesión
              </Button>
            </Stack>
          ) : (
            <>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              <form onSubmit={submit} noValidate>
                <Stack spacing={2}>
                  <TextField
                    label="Email" type="email" fullWidth required autoFocus autoComplete="email"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                  />
                  <Button type="submit" variant="contained" size="large" disabled={loading}>
                    {loading ? 'Enviando...' : 'Enviar enlace'}
                  </Button>
                </Stack>
              </form>
              <Typography variant="body2" textAlign="center" sx={{ mt: 3 }}>
                ¿La recordaste? <Link component={RouterLink} to="/login">Inicia sesión</Link>
              </Typography>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default RecuperarContrasena;
